import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type ApiReportReason = 'harassment' | 'cheating' | 'inappropriate_name' | 'spam' | 'other';
export type ApiReportSeverity = 'low' | 'medium' | 'high';
export type ApiReportStatus = 'pending' | 'actioned' | 'dismissed';
export type ApiReportAction = 'restricted' | 'dismissed';

export interface ApiReportUser {
  id: string;
  username: string;
  email: string;
  avatar?: string | null;
  // Live state of the user's messaging ban — independent of this report's status/actionTaken,
  // since an admin can unrestrict a user at any time via PlayersService.unrestrictMessaging()
  // without reopening or changing the report itself.
  isMessagingRestricted: boolean;
}

export interface ApiReportReporter {
  userId: string;
  username: string | null;
  avatar?: string | null;
  reportedAt: string;
}

export interface ApiReport {
  _id: string;
  // Always populated in practice — the backend never hard-deletes User records
  // (account deletion is a soft isActive:false flag), so reportedUserId always resolves.
  reportedUser: ApiReportUser;
  // From ModerationService.list(): every reporter entry across every report ever filed
  // against this user (full lifetime history, newest first), so reportedBy.length always
  // equals reportCount. Only ModerationService.list() merges history this way — this type
  // is reused for get()/action() responses too, where reportedBy would be scoped to just
  // that single report, but the component never calls those for display (see moderation.component.ts).
  reportedBy: ApiReportReporter[];
  reportCount: number;
  reason: ApiReportReason;
  severity: ApiReportSeverity;
  status: ApiReportStatus;
  description: string;
  context?: string | null;
  createdAt: string;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  actionTaken?: ApiReportAction | null;
  actionNote?: string | null;
}

export interface ReportListFilters {
  status?: 'all' | ApiReportStatus;
  search?: string;
}

export interface ReportStatusCounts {
  pending: number;
  actioned: number;
  dismissed: number;
  all: number;
}

export interface ReportListResponse {
  success: boolean;
  code?: number;
  message?: string;
  data: {
    list: ApiReport[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    statusCounts: ReportStatusCounts;
  };
}

export interface ReportGetResponse {
  success: boolean;
  code?: number;
  message?: string;
  data: ApiReport;
}

export interface ReportActionResponse {
  success: boolean;
  code?: number;
  message?: string;
  data?: ApiReport;
}

const BASE = `${environment.apiUrl}/api/admin/moderation`;

@Injectable({ providedIn: 'root' })
export class ModerationService {
  private http = inject(HttpClient);

  list(page = 1, limit = 20, filters: ReportListFilters = {}): Observable<ReportListResponse> {
    return this.http.post<ReportListResponse>(`${BASE}/list`, { page, limit, filters });
  }

  get(id: string): Observable<ReportGetResponse> {
    return this.http.post<ReportGetResponse>(`${BASE}/get`, { id });
  }

  action(id: string, action: ApiReportAction, note: string): Observable<ReportActionResponse> {
    return this.http.post<ReportActionResponse>(`${BASE}/action`, { id, action, note });
  }
}
